import { http } from '@/apis/aipass'
import { env } from '@/libs/env'
import { createAsyncThunk } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { ReservationForm } from './spaces-slice'

export interface SpaceEstimatePriceAssignSpace {
  spaceId: string
  usageFrom: string
  usageTo: string
}

export interface SpaceEstimatePriceRequest {
  hotelId: string
  reservationId?: string
  assignSpaces: SpaceEstimatePriceAssignSpace[]
}

const isValidTimePart = (v: string | undefined): v is string =>
  v != null && v !== ''

/** 見積もりAPIに渡すリクエストを組み立てる。spaceId / useDay / 利用時間が無い場合は null。終了00時は '24' に正規化。 */
export function buildEstimatePriceRequest(
  formValues: Pick<
    ReservationForm,
    'spaceId' | 'useDay' | 'quantity' | 'usageFromHour' | 'usageFromMinute' | 'usageToHour' | 'usageToMinute'
  >,
  hotelId: string,
  reservationId?: string,
): SpaceEstimatePriceRequest | null {
  if (!formValues.spaceId || !formValues.useDay) {
    return null
  }
  if (
    !isValidTimePart(formValues.usageFromHour) ||
    !isValidTimePart(formValues.usageFromMinute) ||
    !isValidTimePart(formValues.usageToHour) ||
    !isValidTimePart(formValues.usageToMinute)
  ) {
    return null
  }
  const toHour = formValues.usageToHour === '00' ? '24' : formValues.usageToHour
  const quantity = Math.max(1, Number(formValues.quantity) || 1)
  const assignSpaces: SpaceEstimatePriceAssignSpace[] = [...Array(quantity)].map(() => ({
    spaceId: formValues.spaceId,
    usageFrom: dayjs(`${formValues.useDay} ${formValues.usageFromHour}:${formValues.usageFromMinute}`).format('YYYY-MM-DD HH:mm:ss'),
    usageTo: dayjs(`${formValues.useDay} ${toHour}:${formValues.usageToMinute}`).format('YYYY-MM-DD HH:mm:ss'),
  }))
  return { hotelId, reservationId, assignSpaces }
}

/** POST spaces/estimate-price 成功時レスポンス */
export interface SpaceEstimatePriceResponse {
  /** 利用料（割引前・円） */
  subtotalAmount: number
  /** 宿泊者割引額（円） */
  discountAmount: number
  /** 請求金額（円）＝ 利用料 − 宿泊者割引 */
  totalPrice: number
}

export const fetchSpaceEstimatePrice = createAsyncThunk<
  SpaceEstimatePriceResponse,
  SpaceEstimatePriceRequest,
  { rejectValue: unknown }
>(
  'space/fetchSpaceEstimatePrice',
  async (params, { rejectWithValue }) => {
    try {
      const response = await http<SpaceEstimatePriceResponse>({
        method: 'post',
        url: `${env('BASE_API_V3')}/guest/spaces/estimate-price`,
        data: params,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)
